import { useCallback, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Field } from '../../../components/field/field';
import { Typography } from '../../../components/typography/typography';
import { toast } from '../../../util/toasts';
import { Spinner } from '../../../components/spinner/spinner';
import { DateTime } from 'luxon';
import classNames from 'classnames';
import { debounce } from 'lodash';
import usePrevious from '../../../hooks/use-previous';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';

const SEQUENCE_TOKEN = new RegExp('{[Ss][1-8]}+', 'g');
const DATE_TOKEN = new RegExp('{[Yy]{2}}|{[Yy]{4}}|{[Mm]{1,2}}|{[Dd]{1,2}}+', 'g');

export const OrderNumberFormatConfig = () => {
  const { getConfiguration } = useAPI();
  const { updateOrderNumberFormat } = useOrderApiV1();

  const [rawPattern, setRawPattern] = useState<string>();
  const [formattedPattern, setFormattedPattern] = useState<string>();
  const [isValid, setIsValid] = useState(false);
  const previousPattern = usePrevious(rawPattern);
  const time = DateTime.local();

  const sanitizePattern = (pattern: string) => {
    return pattern
      .trim()
      .replace(/\s+/g, '-')
      .replace(SEQUENCE_TOKEN, (match) => match.toUpperCase())
      .replace(DATE_TOKEN, (match) => match.toUpperCase());
  };

  const toFormatPattern = (pattern: string): [string, boolean] => {
    const sanitized = sanitizePattern(pattern);
    const isValid = SEQUENCE_TOKEN.test(sanitized);
    setIsValid(isValid);

    return [
      sanitized
        .replace(DATE_TOKEN, (match) => {
          switch (match) {
            case '{YY}':
              return time.toFormat('yy');
            case '{YYYY}':
              return time.toFormat('yyyy');
            case '{M}':
              return time.toFormat('M');
            case '{MM}':
              return time.toFormat('MM');
            case '{D}':
              return time.toFormat('d');
            case '{DD}':
              return time.toFormat('dd');
            default:
              return match;
          }
        })
        .replace(SEQUENCE_TOKEN, (match) => {
          const length = parseInt(match.slice(2, -1));
          return '1'.padStart(length, '0');
        }),
      isValid,
    ];
  };

  const doUpdate = useCallback(
    debounce((pattern: string) => {
      updateOrderNumberFormat(sanitizePattern(pattern)).catch((err) => {
        console.error(err);
        toast('Failed to update order number format', 'error');
      });
    }, 1000),
    [],
  );

  useEffect(() => {}, [rawPattern]);

  useEffect(() => {
    if (rawPattern === undefined) return;

    const [f, v] = toFormatPattern(rawPattern);
    setFormattedPattern(f);
    previousPattern && previousPattern !== rawPattern && v && doUpdate(rawPattern);
  }, [rawPattern]);

  useEffect(() => {
    getConfiguration('CONF_ORDER_NUMBER_FORMAT')
      .then((res) => setRawPattern(res?.value || ''))
      .catch(() => toast('Failed to load order number format', 'error'));
  }, []);

  return rawPattern !== undefined ? (
    <div className="flex flex-col gap-1">
      <Field type="text" value={rawPattern} onChange={setRawPattern} invalid={!isValid} />
      <Typography
        type="text-xs"
        className={classNames('!italic', { '!text-grey-700': isValid, '!text-error-500': !isValid })}
      >
        Format of the order number.
        <br />
        Result: <b>{formattedPattern}</b>
      </Typography>

      <Typography type="text-xs" className="!text-grey-700 !italic">
        Available tokens:
        <br />- <b>{`{YY}:`}</b> Two digit year
        <br />- <b>{`{YYYY}:`}</b> Four digit year
        <br />- <b>{`{D}:`}</b> Un-padded day of month
        <br />- <b>{`{DD}:`}</b> Day of month padded to 2 digits
        <br />- <b>{`{M}:`}</b> Un-padded numeric month
        <br />- <b>{`{MM}:`}</b> Numeric month padded to 2 digits
        <br />- <b>{`{S[1-8]}:`}</b> Sequence number padded to the specified number of digits, minimum 1, maximum 8. At
        least one sequence token is required in the format.
      </Typography>
    </div>
  ) : (
    <Spinner />
  );
};
